import {
  Button,
  Form,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";

import { formatDateTime } from "../../helpers/date";

import BonusCampaignForm, { getVoucherRewardMode } from "./BonusCampaignForm";
import UserStatusTag from "../shared/UserStatusTag";
import { buildPackageAmounts } from "./packageAmounts";
import { CAMPAIGN_TRIGGERS } from "../../constants/campaign";

const { Text } = Typography;

// Also true once vouchers were sent and later removed from the campaign, so its export stays reachable.
const hasVoucherReward = (record) =>
  record.vouchers?.length > 0 ||
  record.voucherPackages?.length > 0 ||
  record.vouchersSent > 0;

const BonusCampaignList = ({
  campaigns,
  loading,
  canUpdate,
  onToggle,
  onUpdate,
  canExport,
  exportingIds,
  onExport,
  welcomePackageLabels = {},
  vouchers = [],
}) => {
  const [data, setData] = useState(campaigns || []);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editPackageAmounts, setEditPackageAmounts] = useState(
    buildPackageAmounts()
  );
  const [editForm] = Form.useForm();

  useEffect(() => {
    setData(campaigns || []);
  }, [campaigns]);

  const openEditModal = (record) => {
    editForm.resetFields();

    setSelectedCampaign(record);

    const campaignDetails = record.campaignDetails || [];

    const packageAmounts = buildPackageAmounts(
      campaignDetails.map((detail) => detail.packageAmount),
      Object.keys(welcomePackageLabels)
    );

    const campaignDetailsValues = packageAmounts.map((amount) => {
      const match = campaignDetails.find((d) => d.packageAmount === amount);
      return {
        packageAmount: amount,
        bonus: match?.bonus ?? 0,
        limit: match?.limit,
      };
    });

    setEditPackageAmounts(packageAmounts);

    editForm.setFieldsValue({
      name: record.name,
      description: record.description,
      isFirstTopupOnly: record.isFirstTopupOnly || false,
      badge_en: record.badge?.en || "",
      badge_zh: record.badge?.zh || "",
      badge_ms: record.badge?.ms || "",
      campaignDetails: campaignDetailsValues || [],
      category: record.category,
      status: record?.status || "-",
      trigger: record.trigger || CAMPAIGN_TRIGGERS.TOPUP,
      minTopupAmount: record.minTopupAmount ?? undefined,
      vouchers: record.vouchers || [],
      voucherRewardMode: getVoucherRewardMode(record),
      voucherPackages: (record.voucherPackages || []).map((row) => ({
        topupAmount: row.topupAmount,
        voucherId: row.voucherId,
        quantity: row.quantity,
        welcomeVoucherId: row.welcomeVoucherId,
      })),
    });

    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    const isUpdated = await onUpdate(selectedCampaign._id, values);
    if (!isUpdated) return;

    setEditModalVisible(false);
    setSelectedCampaign(null);
    editForm.resetFields();
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setSelectedCampaign(null);
    editForm.resetFields();
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "_id",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Target",
      dataIndex: "category",
      render: (cat) =>
        cat === "userStatus" ? (
          <Tag color="blue">By User Status</Tag>
        ) : (
          <Tag color="green">All Users</Tag>
        ),
    },
    {
      title: "Selected User Status",
      dataIndex: "status",
      render: (text) =>
        text ? <UserStatusTag statusKey={text}>{text}</UserStatusTag> : "-",
    },
    {
      title: "Period",
      render: (_, record) => (
        <span>
          {record.startDate
            ? formatDateTime(record.startDate).substring(0, 10)
            : "-"}{" "}
          →{" "}
          {record.endDate
            ? formatDateTime(record.endDate).substring(0, 10)
            : "-"}
        </span>
      ),
    },
    {
      title: "For First Topup",
      dataIndex: "isFirstTopupOnly",
      render: (text) =>
        text === true ? (
          <Tag color="green">Enable</Tag>
        ) : (
          <Tag color="default">Disable</Tag>
        )
    },
    {
      // One per merchant: matches the export's row count, not the number of voucher copies sent.
      title: "Merchants Rewarded",
      dataIndex: "vouchersSent",
      align: "center",
      render: (count, record) => (hasVoucherReward(record) ? count || 0 : "-"),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      render: (value, record) =>
        canUpdate ? (
          <Switch
            checked={value}
            onChange={(checked) => onToggle(record._id, checked)}
          />
        ) : value ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    ...(canUpdate || canExport
      ? [
        {
          title: "Actions",
          fixed: "right",
          render: (_, record) => (
            <Space>
              {canUpdate && (
                <Button type="default" onClick={() => openEditModal(record)}>
                  Edit
                </Button>
              )}
              {canExport && hasVoucherReward(record) && (
                <Button
                  loading={exportingIds.has(record._id)}
                  onClick={() => onExport(record._id)}
                >
                  Export CSV
                </Button>
              )}
            </Space>
          ),
        },
      ]
      : []),
  ];

  return (
    <>
      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={data}
        loading={false}
        pagination={false}
        scroll={{ x: true }}
      />

      <Modal
        title="Edit Details"
        visible={editModalVisible}
        onCancel={handleEditCancel}
        footer={null} // ✅ let form control submit
        destroyOnClose
        width="60%"
      >
        <div>
          <BonusCampaignForm
            form={editForm}
            loading={loading.create}
            mode="update"
            onSubmit={handleEditSubmit}
            onCancel={handleEditCancel}
            submitText="Update Campaign"
            selectedCampaign={selectedCampaign}
            welcomePackageLabels={welcomePackageLabels}
            packageAmounts={editPackageAmounts}
            vouchers={vouchers}
          />
        </div>
      </Modal>
    </>
  );
};

export default BonusCampaignList;
